import type { Logger } from "../utils/logger";

type AuthContext = {
  accessToken?: string | null;
};

type QueuedTransportOptions<T> = {
  storageKey: string;
  maxBatchSize: number;
  flushIntervalMs: number;
  logger: Logger;
  postBatch: (batch: T[], auth: AuthContext) => Promise<void>;
};

export type QueuedTransport<T> = {
  enqueue: (item: T) => void;
  flush: () => Promise<void>;
  setAuthToken: (token: string | null) => void;
  size: () => number;
  clear: () => void;
};

export function createQueuedTransport<T>(options: QueuedTransportOptions<T>): QueuedTransport<T> {
  const { storageKey, maxBatchSize, flushIntervalMs, postBatch, logger } = options;

  let queue: T[] = [];
  let flushTimeout: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 0;
  let auth: AuthContext = { accessToken: null };
  let initialized = false;
  let onlineListenerAttached = false;

  function loadQueue() {
    if (initialized) return;
    initialized = true;
    try {
      const stored = typeof localStorage !== "undefined" ? localStorage.getItem(storageKey) : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          queue = parsed;
        }
      }
    } catch (error) {
      logger.warn("Unable to load queued transport state", error);
    }
  }

  function persistQueue() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(storageKey, JSON.stringify(queue));
      }
    } catch (error) {
      logger.warn("Unable to persist queued transport state", error);
    }
  }

  function scheduleFlush() {
    if (flushTimeout !== null) {
      return;
    }
    flushTimeout = setTimeout(() => {
      flushTimeout = null;
      void flush();
    }, flushIntervalMs + backoffMs);
  }

  async function flush() {
    loadQueue();
    if (queue.length === 0) {
      return;
    }

    const batch = queue.slice(0, maxBatchSize);
    const remaining = queue.slice(maxBatchSize);

    try {
      await postBatch(batch, auth);
      queue = remaining;
      persistQueue();
      backoffMs = 0;
      if (queue.length > 0) {
        scheduleFlush();
      }
    } catch (error) {
      logger.warn("Queued transport flush failed", error);
      backoffMs = Math.min(10000, backoffMs === 0 ? 1000 : backoffMs * 2);
      scheduleFlush();
    }
  }

  function attachOnlineListener() {
    if (onlineListenerAttached || typeof window === "undefined") {
      return;
    }
    window.addEventListener(
      "online",
      () => {
        backoffMs = 0;
        void flush();
      },
      { once: true }
    );
    onlineListenerAttached = true;
  }

  function enqueue(item: T) {
    loadQueue();
    queue.push(item);
    persistQueue();
    if (typeof window !== "undefined") {
      window.requestIdleCallback?.(() => void flush());
      attachOnlineListener();
    }
    scheduleFlush();
  }

  function setAuthToken(token: string | null) {
    auth = { ...auth, accessToken: token };
  }

  function size() {
    loadQueue();
    return queue.length;
  }

  function clear() {
    loadQueue();
    queue = [];
    persistQueue();
  }

  return {
    enqueue,
    flush,
    setAuthToken,
    size,
    clear
  };
}
